/**
 * Size recommendation ("Ganz sicher gehen?" block).
 *
 * Lives inside the size guide modal of the main product page and turns a measured wrist
 * circumference into the matching size, which can then be applied to the variant picker.
 *
 * Rule:
 *   MIN_CM <= wrist <  LARGE_SIZE_FROM_CM  → the smaller size (M)
 *   LARGE_SIZE_FROM_CM <= wrist <= max     → the larger size (L)
 *   anything else                          → no standard size fits and the customer is pointed at
 *                                            support, who adjust the bracelet to their measurement
 */

const SIZES = [
  { code: 'M', cm: 20 },
  { code: 'L', cm: 22 },
];

const MIN_CM = 16.5;
const MAX_CM = 19.5;
const LARGE_SIZE_FROM_CM = 19;

/**
 * These two bracelets are made from a longer band and therefore still fit a wrist that is too
 * large for the rest of the range. Keyed by product ID.
 */
const MAX_CM_OVERRIDES = new Map([
  ['6674993086615', 20],
  ['7441893982440', 20],
]);

const HIDE_ANIMATION_TIMEOUT = 400;

/**
 * The block is behind an A/B test, so it is hidden in the markup and only revealed for the
 * treatment group. The experiment is activated when the size guide opens, so that customers who
 * never look at the size guide are kept out of the sample.
 */
const activatedExperiments = new Set();

/**
 * Tells Convert to run an experiment, in the same way the engraving preview does.
 *
 * @param {string} experimentId
 */
function activateExperiment(experimentId) {
  if (!experimentId || activatedExperiments.has(experimentId)) return;

  activatedExperiments.add(experimentId);

  window.experiments = window.experiments || {};
  window.experiments[experimentId] = {
    triggered: true,
    variation: null,
  };

  window._conv_q = window._conv_q || [];
  window._conv_q.push(['executeExperiment', experimentId]);
}

/**
 * Selecting a size re-renders the variant picker block, which throws away the confirmation
 * element. The applied size is therefore remembered here and restored by the freshly rendered
 * element. Keyed by form and option position, so each size option keeps its own state.
 */
const appliedSizes = new Map();

const GTAG_POLL_INTERVAL = 100;
const GTAG_POLL_ATTEMPTS = 100;

/**
 * Sends an event to Google Analytics.
 *
 * This mirrors `tracker.googleAnalyticsV2` in cell-core, which a plain browser module cannot
 * import: the gtag snippet arrives through `content_for_header` and may not have run yet, so the
 * send is retried until it is there. Unlike the shared helper the retrying gives up eventually,
 * rather than leaving a timer running for the life of a page that never loads gtag at all.
 *
 * The declarative `data-analytics-*` attributes are not an option here: the modal re-appends itself
 * to the body every time it opens, which makes the analytics client attach a second click listener
 * to every node inside it, and the events would be sent more than once.
 *
 * @param {string} name
 * @param {Object} [params]
 */
function trackEvent(name, params = {}) {
  let attempts = 0;

  const send = () => {
    if (!window.gtag) {
      attempts += 1;

      if (attempts <= GTAG_POLL_ATTEMPTS) {
        window.setTimeout(send, GTAG_POLL_INTERVAL);
      }

      return;
    }

    window.gtag('event', name, {
      ...params,

      page_title: document.title,
      page_location: window.location.href,
      page_path: window.location.pathname,
    });
  };

  send();
}

/**
 * Parses a measurement that may use either a comma or a dot as decimal separator.
 *
 * @param {string} value
 * @returns {number|null}
 */
function parseMeasurement(value) {
  const normalizedValue = String(value || '')
    .trim()
    .replace(',', '.');

  if (normalizedValue === '') return null;

  const measurement = Number(normalizedValue);

  return Number.isFinite(measurement) ? measurement : null;
}

/**
 * Replaces the `%token%` placeholders of a translated string.
 *
 * @param {string} template
 * @param {Object<string, string>} values
 * @returns {string}
 */
function fillTemplate(template, values) {
  return Object.entries(values).reduce(
    (filledTemplate, [key, value]) => filledTemplate.split(`%${key}%`).join(value),
    template || '',
  );
}

/**
 * Formats a number for the current storefront language, so German shows `16,5` and English `16.5`.
 *
 * The fraction digits are raised to the maximum the Intl API allows, so that a measurement is
 * echoed back exactly as it was entered. The default of three would round `16,5555` to `16,556`.
 *
 * @param {number} value
 * @returns {string}
 */
function formatNumber(value) {
  try {
    return value.toLocaleString(document.documentElement.lang || undefined, {
      maximumFractionDigits: 20,
    });
  } catch (error) {
    return String(value);
  }
}

class SizeRecommendation extends HTMLElement {
  connectedCallback() {
    // The modal moves itself to the body when it opens, which disconnects and reconnects this
    // element. Only the first connection sets everything up.
    if (this.mounted) return;
    this.mounted = true;

    this.sizes = this.resolveSizes();

    // The rules are written for exactly two sizes, so anything else leaves the size guide exactly
    // as it was before.
    if (this.sizes.length !== 2) {
      this.remove();

      return;
    }

    this.currentSize = null;
    this.measurement = null;

    this.restoreConfirmation();

    this.watchModal();

    this.trigger.addEventListener('click', this.handleTriggerClick.bind(this));
    this.input.addEventListener('input', this.handleInput.bind(this));
    this.submitButton.addEventListener('click', this.handleSubmitClick.bind(this));
    this.applyButton.addEventListener('click', this.handleApplyClick.bind(this));
  }

  /**
   * Activates the experiment as soon as the size guide is opened. "dialog:before-show" is used
   * rather than "dialog:after-show", as the latter only fires once the enter animation has played
   * and the block would visibly pop in afterwards.
   */
  watchModal() {
    const modal = this.closest('x-modal');

    if (!modal) return;

    modal.addEventListener('dialog:before-show', () => {
      activateExperiment(this.dataset.experimentId);
    });
  }

  /**
   * Maps the option values onto the size table. The band length is read from the option value
   * itself when it carries one (`M = 20 cm`), otherwise it is looked up by size code (`M`).
   *
   * @returns {{ id: string, code: string, cm: number }[]}
   */
  resolveSizes() {
    let optionValues = [];

    try {
      optionValues = JSON.parse(this.dataset.optionValues || '[]');
    } catch (error) {
      return [];
    }

    return optionValues
      .map((optionValue) => {
        const name = String(optionValue.name || '')
          .trim()
          .toUpperCase();

        const lengthInName = name.match(/(\d+(?:[.,]\d+)?)\s*CM/);
        const codeInName = name.match(/^[A-ZÄÖÜ]+/);
        const knownSize = SIZES.find((size) => codeInName && size.code === codeInName[0]);

        let cm = null;

        if (lengthInName) {
          cm = Number(lengthInName[1].replace(',', '.'));
        } else if (knownSize) {
          cm = knownSize.cm;
        }

        if (cm === null || !Number.isFinite(cm)) return null;

        return {
          id: String(optionValue.id),
          code: knownSize ? knownSize.code : (codeInName && codeInName[0]) || name,
          cm,
        };
      })
      .filter(Boolean)
      .sort((a, b) => a.cm - b.cm);
  }

  /**
   * The largest wrist that the range still covers, which a few products extend.
   *
   * @returns {number}
   */
  get maxCm() {
    return MAX_CM_OVERRIDES.get(this.getAttribute('product-id')) ?? MAX_CM;
  }

  /**
   * @param {number|null} measurement
   * @returns {{ id: string, code: string, cm: number }|null}
   */
  recommendSize(measurement) {
    if (measurement === null || measurement < MIN_CM || measurement > this.maxCm) return null;

    return measurement < LARGE_SIZE_FROM_CM ? this.sizes[0] : this.sizes[1];
  }

  handleTriggerClick() {
    this.panel.hidden = false;
    this.trigger.setAttribute('aria-expanded', 'true');
    this.trigger.hidden = true;

    // The trigger that was focused is now hidden, so focus is moved into the panel.
    this.panel.focus();

    trackEvent('open_size_calculator');
  }

  handleInput() {
    this.sanitizeMeasurement();

    // A previous result must not linger while the measurement is being changed.
    this.currentSize = null;
    this.result.hidden = true;
    this.error.hidden = true;
    this.outOfRange.hidden = true;
  }

  /**
   * Keeps the field to digits and a single decimal separator, so a measurement can never be
   * anything but a number. A native number input is not used, as it rejects the comma that German
   * customers type.
   */
  sanitizeMeasurement() {
    const { value } = this.input;

    let sanitizedValue = value.replace(/[^\d.,]/g, '');

    const separatorIndex = sanitizedValue.search(/[.,]/);

    if (separatorIndex !== -1) {
      sanitizedValue =
        sanitizedValue.slice(0, separatorIndex + 1) +
        sanitizedValue.slice(separatorIndex + 1).replace(/[.,]/g, '');
    }

    if (sanitizedValue === value) return;

    // Removing characters moves the caret to the end, so it is put back where it was.
    const caretPosition = Math.max(
      0,
      (this.input.selectionStart || 0) - (value.length - sanitizedValue.length),
    );

    this.input.value = sanitizedValue;
    this.input.setSelectionRange(caretPosition, caretPosition);
  }

  handleSubmitClick() {
    const measurement = parseMeasurement(this.input.value);
    const size = this.recommendSize(measurement);

    trackEvent('calculate_size', {
      title: size ? size.code : 'invalid',
      value: measurement === null ? 0 : measurement,
    });

    if (!size) {
      this.currentSize = null;
      this.result.hidden = true;

      // A measurement that is simply outside the range means no standard size fits, which is what
      // the support hint is for. An empty field only needs to be filled in.
      this.error.hidden = measurement !== null;
      this.outOfRange.hidden = measurement === null;

      return;
    }

    this.currentSize = size;
    this.measurement = measurement;

    const values = {
      size: size.code,
      cm: formatNumber(size.cm),
      wrist: formatNumber(measurement),
    };

    this.resultSize.textContent = fillTemplate(this.dataset.resultSize, values);
    this.explanation.textContent = fillTemplate(this.dataset.explanation, values);
    this.applyButton.textContent = fillTemplate(this.dataset.apply, values);

    this.error.hidden = true;
    this.outOfRange.hidden = true;
    this.result.hidden = false;

    window.requestAnimationFrame(() => {
      this.result.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    });
  }

  async handleApplyClick() {
    const size = this.currentSize;

    if (!size) return;

    trackEvent('apply_recommended_size', {
      title: size.code,
      value: this.measurement,
    });

    const confirmation = fillTemplate(this.dataset.confirmation, {
      size: size.code,
      cm: formatNumber(size.cm),
      wrist: formatNumber(this.measurement),
    });

    appliedSizes.set(this.storageKey, {
      optionValueId: size.id,
      confirmation,
    });

    this.showConfirmation(confirmation);

    // The modal appends itself to the body while it is open and only moves back once it is
    // hidden, so it has to be closed before the variant change re-renders the variant picker.
    await this.closeModal();

    this.selectSize(size);
  }

  /**
   * `hide()` resolves once the leave animation has finished, but it waits for an event that may
   * never arrive if the animation is interrupted, so it is raced against a timeout. Selecting the
   * size must not depend on the modal reporting back.
   *
   * @returns {Promise<unknown>}
   */
  closeModal() {
    const modal = this.closest('x-modal');

    if (!modal || typeof modal.hide !== 'function') return Promise.resolve();

    return Promise.race([
      modal.hide(),
      new Promise((resolve) => {
        window.setTimeout(resolve, HIDE_ANIMATION_TIMEOUT);
      }),
    ]);
  }

  /**
   * @param {{ id: string }} size
   */
  selectSize(size) {
    const input = this.findOptionInput(size);

    if (!input || input.checked) return;

    input.checked = true;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /**
   * The option inputs are associated with the product form through their `form` attribute rather
   * than by being nested inside it, so they are looked up through the form's element collection.
   *
   * @param {{ id: string }} size
   * @returns {HTMLInputElement|undefined}
   */
  findOptionInput(size) {
    const form = document.forms[this.getAttribute('form-id')];

    if (!form) return undefined;

    const optionPosition = this.getAttribute('option-position');

    return Array.from(form.elements).find(
      (element) =>
        element.type === 'radio' &&
        element.dataset.optionPosition === optionPosition &&
        element.value === size.id,
    );
  }

  /**
   * Re-applies the confirmation after the variant picker has been re-rendered. It is dropped as
   * soon as the customer selects a different size themselves.
   */
  restoreConfirmation() {
    const appliedSize = appliedSizes.get(this.storageKey);

    if (!appliedSize) return;

    const input = this.findOptionInput({ id: appliedSize.optionValueId });

    if (!input || !input.checked) {
      appliedSizes.delete(this.storageKey);
      this.hideConfirmation();

      return;
    }

    this.showConfirmation(appliedSize.confirmation);
  }

  /**
   * The confirmation belongs next to the option values rather than inside this element, and the
   * variant picker owns that markup. It is created on demand instead of being rendered by Liquid,
   * which keeps the whole feature out of the variant picker snippet.
   *
   * @param {string} text
   */
  showConfirmation(text) {
    const fieldset = this.optionFieldset;

    if (!fieldset) return;

    let confirmation = fieldset.querySelector('.size-recommendation-confirmation');

    if (!confirmation) {
      confirmation = document.createElement('p');
      confirmation.className = 'size-recommendation-confirmation';

      fieldset.append(confirmation);
    }

    confirmation.textContent = text;
    confirmation.hidden = false;
  }

  hideConfirmation() {
    const confirmation = this.optionFieldset?.querySelector('.size-recommendation-confirmation');

    if (confirmation) confirmation.hidden = true;
  }

  /**
   * The fieldset holding this option's values, reached through one of the option inputs. Not every
   * value necessarily has one — a combined product renders links instead — so each is tried.
   *
   * @returns {HTMLElement|null}
   */
  get optionFieldset() {
    const input = this.sizes.map((size) => this.findOptionInput(size)).find(Boolean);

    return input ? input.closest('fieldset') : null;
  }

  /**
   * @returns {string}
   */
  get storageKey() {
    return `${this.getAttribute('form-id')}/${this.getAttribute('option-position')}`;
  }

  get trigger() {
    return this.querySelector('[data-size-recommendation-trigger]');
  }

  get panel() {
    return this.querySelector('[data-size-recommendation-panel]');
  }

  get input() {
    return this.querySelector('[data-size-recommendation-input]');
  }

  get submitButton() {
    return this.querySelector('[data-size-recommendation-submit]');
  }

  get result() {
    return this.querySelector('[data-size-recommendation-result]');
  }

  get resultSize() {
    return this.querySelector('[data-size-recommendation-result-size]');
  }

  get explanation() {
    return this.querySelector('[data-size-recommendation-explanation]');
  }

  get applyButton() {
    return this.querySelector('[data-size-recommendation-apply]');
  }

  get error() {
    return this.querySelector('[data-size-recommendation-error]');
  }

  get outOfRange() {
    return this.querySelector('[data-size-recommendation-out-of-range]');
  }
}

if (!window.customElements.get('size-recommendation')) {
  window.customElements.define('size-recommendation', SizeRecommendation);
}
